import React, { useEffect, useState } from "react";
import axios from "axios";
import Modal from "@mui/material/Modal";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";

function App() {
  const [apis, setApis] = useState([]);
  const [selectedApi, setSelectedApi] = useState(null);
  const [open, setOpen] = useState(false);

  const authUsername = "gem_api_user";
  const authPassword = "QAK76676";

  const fetchData = async () => {
    const encodedCredentials = btoa(`${authUsername}:${authPassword}`);

    try {
      const response = await axios.get("https://gemapi.app/api/get_apis", {
        headers: {
          Authorization: `Basic ${encodedCredentials}`,
        },
      });
      setApis(response.data);
    } catch (error) {
      console.error("Error fetching API data:", error);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenModal = (api) => {
    setSelectedApi(api);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedApi(null);
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>API Management</h1>
        <p style={styles.subtitle}>Monitoring {apis.length} APIs</p>
      </header>
      <div style={styles.grid}>
        {apis.map((api, index) => (
          <div
            key={index}
            style={{
              ...styles.card,
              ...(api.current_status === "up" ? styles.cardUp : styles.cardDown),
            }}
            onClick={() => handleOpenModal(api)}
          >
            <h2 style={styles.apiName}>{api.name}</h2>
            <p style={styles.latency}>{Math.round(api.latency_1)} ms</p>
            <div style={{
              ...styles.statusIndicator,
              backgroundColor: api.current_status === "up" ? "#34C759" : "#FF3B30",
            }}>
              {api.current_status === "up" ? "Online" : "Offline"}
            </div>
          </div>
        ))}
      </div>

      {selectedApi && (
        <Modal open={open} onClose={handleCloseModal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>{selectedApi.name}</h2>
            <div style={styles.modalInfo}>
              <div style={styles.modalInfoItem}>
                <span style={styles.modalInfoLabel}>Status</span>
                <span style={{
                  ...styles.modalInfoValue,
                  color: selectedApi.current_status === "up" ? "#34C759" : "#FF3B30",
                }}>
                  {selectedApi.current_status === "up" ? "Online" : "Offline"}
                </span>
              </div>
              <div style={styles.modalInfoItem}>
                <span style={styles.modalInfoLabel}>Latency</span>
                <span style={styles.modalInfoValue}>{Math.round(selectedApi.latency_1)} ms</span>
              </div>
            </div>
            <div style={styles.chartWrapper}>
              <LatencyChart
                latencies={[
                  { time: selectedApi.time_12, latency: Math.round(selectedApi.latency_12) },
                  { time: selectedApi.time_11, latency: Math.round(selectedApi.latency_11) },
                  { time: selectedApi.time_10, latency: Math.round(selectedApi.latency_10) },
                  { time: selectedApi.time_9, latency: Math.round(selectedApi.latency_9) },
                  { time: selectedApi.time_8, latency: Math.round(selectedApi.latency_8) },
                  { time: selectedApi.time_7, latency: Math.round(selectedApi.latency_7) },
                  { time: selectedApi.time_6, latency: Math.round(selectedApi.latency_6) },
                  { time: selectedApi.time_5, latency: Math.round(selectedApi.latency_5) },
                  { time: selectedApi.time_4, latency: Math.round(selectedApi.latency_4) },
                  { time: selectedApi.time_3, latency: Math.round(selectedApi.latency_3) },
                  { time: selectedApi.time_2, latency: Math.round(selectedApi.latency_2) },
                  { time: selectedApi.time_1, latency: Math.round(selectedApi.latency_1) },
                ]}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const LatencyChart = ({ latencies }) => {
  const data = latencies
    .filter((entry) => entry.time && entry.latency !== undefined)
    .map((entry) => ({
      time: new Date(entry.time).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      latency: Math.round(entry.latency),
    }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA" />
        <XAxis dataKey="time" tick={{ fontSize: 12 }} tickMargin={5} stroke="#8E8E93" />
        <YAxis tick={{ fontSize: 12 }} tickMargin={5} stroke="#8E8E93" />
        <Tooltip
          contentStyle={{
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            border: "none",
            borderRadius: "10px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
          formatter={(value) => [`${Math.round(value)} ms`, "Latency"]}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="latency"
          stroke="#007AFF"
          strokeWidth={2}
          dot={{ fill: "#007AFF", strokeWidth: 2 }}
          activeDot={{ r: 8 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const styles = {
  container: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    padding: "clamp(20px, 5vw, 40px)",
    backgroundColor: "#F2F2F7",
    minHeight: "100vh",
    color: "#1C1C1E",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  header: {
    textAlign: "center",
    marginBottom: "clamp(20px, 5vw, 40px)",
    width: "100%",
  },
  title: {
    fontSize: "clamp(2rem, 5vw, 3rem)",
    fontWeight: "700",
    color: "#1C1C1E",
    marginBottom: "clamp(5px, 2vw, 10px)",
  },
  subtitle: {
    fontSize: "clamp(1rem, 2vw, 1.2rem)",
    color: "#8E8E93",
    fontWeight: "400",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(clamp(200px, 30vw, 300px), 1fr))",
    gap: "clamp(15px, 3vw, 30px)",
    width: "100%",
    maxWidth: "1200px",
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: "15px",
    padding: "clamp(10px, 2vw, 15px)", // Adjusted padding
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    aspectRatio: "1 / 1", // Maintain aspect ratio
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    padding: "clamp(20px, 5vw, 40px)",
    borderRadius: "20px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
    maxWidth: "90%",
    width: "clamp(300px, 90%, 800px)",
    margin: "50px auto",
    outline: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif", // Same as container font
    color: "#1C1C1E", // Same text color
  },
  cardUp: {
    borderTop: "5px solid #34C759",
  },
  cardDown: {
    borderTop: "5px solid #FF3B30",
  },
  apiName: {
    fontSize: "clamp(1.2rem, 3vw, 1.5rem)",
    fontWeight: "600",
    marginBottom: "clamp(10px, 2vw, 15px)",
    color: "#1C1C1E",
    textAlign: "center",
  },
  latency: {
    fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
    fontWeight: "700",
    marginBottom: "clamp(10px, 2vw, 15px)",
    color: "#007AFF",
  },
  statusIndicator: {
    padding: "5px 15px",
    borderRadius: "20px",
    fontSize: "clamp(0.8rem, 1.5vw, 0.9rem)",
    fontWeight: "600",
    color: "#FFFFFF",
  },
  modalTitle: {
    fontSize: "clamp(1.5rem, 4vw, 2rem)",
    fontWeight: "700",
    marginBottom: "clamp(15px, 3vw, 20px)",
    color: "#1C1C1E",
    textAlign: "center",
  },
  modalInfo: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    marginBottom: "clamp(20px, 4vw, 30px)",
    width: "100%",
  },
  modalInfoItem: {
    margin: "clamp(10px, 2vw, 20px)",
    textAlign: "center",
  },
  modalInfoLabel: {
    fontSize: "clamp(0.9rem, 1.8vw, 1rem)",
    color: "#8E8E93",
    display: "block",
    marginBottom: "5px",
  },
  modalInfoValue: {
    fontSize: "clamp(1.2rem, 2.5vw, 1.5rem)",
    fontWeight: "600",
    color: "#1C1C1E",
  },
  chartWrapper: {
    marginTop: "clamp(20px, 4vw, 30px)",
    width: "100%",
    height: "300px",
  },
};

export default App;